import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import api from '../../../api/client'
import { persistAuthSession } from '../login/utils/authSession'

export default function AuthCallbackScreen() {
  const location = useLocation()
  const navigate = useNavigate()
  const [statusMessage, setStatusMessage] = useState('Authenticating session…')
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let isMounted = true

    async function processHandoff() {
      const params = new URLSearchParams(location.search || '')
      const token = params.get('token')
      const tenant = params.get('tenant')
      const remember = params.get('remember') === '1'
      const redirect = params.get('redirect') || '/dashboard/personal'

      if (!token) {
        if (!isMounted) return
        setHasError(true)
        setStatusMessage('No authentication token provided.')
        toast.error('Session handoff failed: missing authorization token.')
        navigate('/login', { replace: true })
        return
      }

      try {
        // Temporarily store token so client interceptor sends it
        localStorage.setItem('authToken', token)

        if (tenant) {
          localStorage.setItem('rememberTenantUsername', tenant.toUpperCase())
        }

        // Fetch authenticated user profile & active modules
        const res = await api.get('/auth/me')
        const userData = res.data

        persistAuthSession({
          token,
          user: userData,
          rememberDevice: remember,
          identifier: userData?.email || '',
        })

        if (!isMounted) return
        toast.success(`Welcome back, ${userData?.name || 'User'}!`)
        navigate(redirect, { replace: true })
      } catch (err) {
        if (!isMounted) return
        localStorage.removeItem('authToken')
        setHasError(true)
        setStatusMessage('Authentication handoff failed. Please sign in manually.')
        toast.error('Session could not be verified. Please sign in.')
        navigate('/login', { replace: true })
      }
    }

    processHandoff()

    return () => {
      isMounted = false
    }
  }, [location.search, navigate])

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <div className="flex flex-col items-center max-w-sm w-full p-8 rounded-2xl bg-surface-container-lowest shadow-lg border border-outline-variant/30 text-center animate-fade-in">
        {!hasError ? (
          <div className="w-12 h-12 rounded-full border-3 border-primary border-t-transparent animate-spin mb-4" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-error/10 text-error flex items-center justify-center text-xl mb-4">
            <span className="material-symbols-rounded">error</span>
          </div>
        )}
        <h2 className="text-lg font-bold font-display text-on-surface mb-1">
          {hasError ? 'Authentication Issue' : 'Entering Boss Global'}
        </h2>
        <p className="text-sm text-on-surface-variant leading-relaxed">
          {statusMessage}
        </p>
      </div>
    </div>
  )
}
